import { useState, useEffect, useCallback } from 'react';
import { useTheme } from "../../hooks/useTheme";
import { useAuth } from "../../hooks/useAuth";
import { Upload, BarChart3, Building2, Briefcase, Search, Filter, X, ChevronUp, ChevronDown, ExternalLink, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelImport from "../../components/jobManagement/ExcelImport";
import EmployerReportingTab from "../../components/jobManagement/EmployerReportingTab";
import api from "../../api/api";

const LOCATIONS = [
    'Atlanta, GA', 'Austin, TX', 'Boston, MA', 'Chicago, IL', 'Dallas, TX',
    'Denver, CO', 'Houston, TX', 'Los Angeles, CA', 'Miami, FL', 'New York, NY',
    'Philadelphia, PA', 'Phoenix, AZ', 'Portland, OR', 'San Francisco, CA',
    'Seattle, WA', 'Washington, DC', 'Remote', 'Hybrid',
];

function CompanyDetailsTab({ isDarkMode }) {
    const [form, setForm] = useState({
        company_name: '',
        job_title: '',
        company_website: '',
        additional_notes: '',
        location: '',
        industry_id: '',
        career_site_url: '',
        workday_url: '',
    });
    const [industries, setIndustries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        api.get('/jobs/industries')
            .then(res => setIndustries(res.data.industries || []))
            .catch(() => {});

        api.get('/employer/company')
            .then(res => {
                const d = res.data;
                setForm({
                    company_name: d.CompanyName || '',
                    job_title: d.JobTitle || '',
                    company_website: d.CompanyWebsite || '',
                    additional_notes: d.AdditionalNotes || '',
                    location: d.Location || '',
                    industry_id: d.IndustryID ? String(d.IndustryID) : '',
                    career_site_url: d.CareerSiteURL || '',
                    workday_url: d.WorkdayURL || '',
                });
            })
            .catch(err => setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to load company details.' }))
            .finally(() => setLoading(false));
    }, []);

    const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

    const handleSubmit = async e => {
        e.preventDefault();
        setSaving(true);
        setMessage(null);
        try {
            await api.patch('/employer/company', {
                ...form,
                industry_id: form.industry_id ? Number(form.industry_id) : null,
            });
            setMessage({ type: 'success', text: 'Company details updated.' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save.' });
        } finally {
            setSaving(false);
        }
    };

    const inputClass = `w-full rounded-lg px-3 py-2 text-sm border focus:outline-none focus:ring-2 focus:ring-primary-500 ${
        isDarkMode
            ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400'
            : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400'
    }`;

    const labelClass = `block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`;

    if (loading) return <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Loading…</p>;

    return (
        <form onSubmit={handleSubmit} className="space-y-4 max-w-lg">
            <div>
                <label className={labelClass}>Company Name</label>
                <p className={`text-sm px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-slate-700/50 border-slate-600 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-700'}`}>
                    {form.company_name || '—'}
                </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={labelClass}>Location</label>
                    <select name="location" value={form.location} onChange={handleChange} className={inputClass}>
                        <option value="">— Select —</option>
                        {LOCATIONS.map(loc => (
                            <option key={loc} value={loc}>{loc}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className={labelClass}>Industry</label>
                    <select name="industry_id" value={form.industry_id} onChange={handleChange} className={inputClass}>
                        <option value="">— Select —</option>
                        {industries.map(ind => (
                            <option key={ind.IndustryID} value={ind.IndustryID}>{ind.IndustryName}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div>
                <label className={labelClass}>Career Site URL</label>
                <input name="career_site_url" value={form.career_site_url} onChange={handleChange} className={inputClass} placeholder="https://careers.example.com" />
            </div>
            <div>
                <label className={labelClass}>Workday URL</label>
                <input name="workday_url" value={form.workday_url} onChange={handleChange} className={inputClass} placeholder="https://example.wd1.myworkdayjobs.com/..." />
            </div>
            <div>
                <label className={labelClass}>Company Website</label>
                <input name="company_website" value={form.company_website} onChange={handleChange} className={inputClass} placeholder="https://example.com" />
            </div>

            <hr className={isDarkMode ? 'border-slate-700' : 'border-slate-200'} />

            <div>
                <label className={labelClass}>Your Job Title</label>
                <input name="job_title" value={form.job_title} onChange={handleChange} className={inputClass} placeholder="e.g. HR Manager" />
            </div>
            <div>
                <label className={labelClass}>Additional Notes</label>
                <textarea name="additional_notes" value={form.additional_notes} onChange={handleChange} rows={3} className={inputClass} />
            </div>

            {message && (
                <p className={`text-sm ${message.type === 'success' ? 'text-primary-500' : 'text-red-500'}`}>
                    {message.text}
                </p>
            )}

            <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50"
            >
                {saving ? 'Saving…' : 'Save Changes'}
            </button>
        </form>
    );
}

const TIME_TYPES = ['Full-Time', 'Part-Time', 'Contract', 'Internship'];

function SortIcon({ field, sortBy }) {
    if (sortBy.field !== field) return <span className="inline-block w-3" />;
    return sortBy.direction === 'asc'
        ? <ChevronUp size={13} className="inline-block ml-1" />
        : <ChevronDown size={13} className="inline-block ml-1" />;
}

function EmployerJobListingsTab({ isDarkMode }) {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({ location: '', timeType: '' });
    const [showFilters, setShowFilters] = useState(false);
    const [sortBy, setSortBy] = useState({ field: 'posted', direction: 'desc' });

    const fetchJobs = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, per_page: 20, sort_by: sortBy.field, sort_dir: sortBy.direction };
            if (search.trim()) params.search = search.trim();
            if (filters.location) params.location = filters.location;
            if (filters.timeType) params.timeType = filters.timeType;
            const res = await api.get('/employer/jobs', { params });
            const data = res.data || {};
            setJobs(Array.isArray(data.jobs) ? data.jobs : []);
            setTotal(Number(data.total || 0));
            setTotalPages(Number(data.total_pages || 0));
        } catch {
            setJobs([]);
            setTotal(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    }, [page, search, filters, sortBy]);

    useEffect(() => { fetchJobs(); }, [fetchJobs]);

    const handleSort = (field) => {
        setSortBy(prev => ({ field, direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc' }));
        setPage(1);
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPage(1);
    };

    const handleSearchSubmit = (e) => { e.preventDefault(); setPage(1); };

    const clearAll = () => {
        setSearch('');
        setFilters({ location: '', timeType: '' });
        setPage(1);
    };

    const activeFilterCount = (search.trim() ? 1 : 0) + Object.values(filters).filter(Boolean).length;

    const handleDownload = useCallback(async (format) => {
        const params = { page: 1, per_page: 1000, sort_by: sortBy.field, sort_dir: sortBy.direction };
        if (search.trim()) params.search = search.trim();
        if (filters.location) params.location = filters.location;
        if (filters.timeType) params.timeType = filters.timeType;
        const res = await api.get('/employer/jobs', { params });
        const allJobs = res.data?.jobs || [];

        const rows = allJobs.map(j => ({
            'Job Title': j.JobTitle || '',
            'Location': j.JobLocation || '',
            'Type': j.TimeType || '',
            'Posted': j.JobPostedOn ? new Date(j.JobPostedOn).toLocaleDateString() : '',
            'Status': j.Status || '',
            'URL': j.JobLinkURL || '',
        }));

        if (format === 'excel') {
            const wb = XLSX.utils.book_new();

            const summaryData = [
                ['Generated', new Date().toLocaleString()],
                ['Total Listings', allJobs.length],
                ['Search', search.trim() || '—'],
                ['Location Filter', filters.location || '—'],
                ['Type Filter', filters.timeType || '—'],
                ['Sort By', `${sortBy.field} (${sortBy.direction})`],
            ];
            XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryData), 'Summary');

            const ws = XLSX.utils.json_to_sheet(rows);
            XLSX.utils.book_append_sheet(wb, ws, 'Job Listings');
            XLSX.writeFile(wb, 'job-listings.xlsx');
        } else {
            const doc = new jsPDF();
            doc.setFontSize(14);
            doc.text('Job Listings', 14, 16);
            autoTable(doc, {
                startY: 22,
                head: [['Job Title', 'Location', 'Type', 'Posted', 'Status']],
                body: rows.map(r => [r['Job Title'], r['Location'], r['Type'], r['Posted'], r['Status']]),
                styles: { fontSize: 8 },
                headStyles: { fillColor: [16, 185, 129] },
            });
            doc.save('job-listings.pdf');
        }
    }, [search, filters, sortBy]);

    const inputCls = `rounded-lg px-3 py-2 text-sm border focus:outline-none focus:ring-2 focus:ring-primary-500 ${
        isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400' : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400'
    }`;
    const thCls = `px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide cursor-pointer select-none ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`;
    const tdCls = `px-4 py-3 text-sm ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`;

    return (
        <div className="space-y-4">
            {/* Search + filter toolbar */}
            <div className="flex flex-wrap items-center gap-2">
                <form onSubmit={handleSearchSubmit} className="flex gap-2 flex-1 min-w-0">
                    <div className="relative flex-1 min-w-0">
                        <Search size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`} />
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search job title…"
                            className={`${inputCls} pl-8 w-full`}
                        />
                    </div>
                    <button type="submit" className="px-3 py-2 text-sm font-medium rounded-lg bg-primary-500 hover:bg-primary-600 text-white">Search</button>
                </form>
                <button
                    onClick={() => setShowFilters(f => !f)}
                    className={`flex items-center gap-1 px-3 py-2 text-sm rounded-lg border font-medium ${
                        showFilters
                            ? 'bg-primary-500 text-white border-primary-500'
                            : isDarkMode ? 'border-slate-600 text-slate-300 hover:text-slate-100' : 'border-slate-300 text-slate-600 hover:text-slate-900'
                    }`}
                >
                    <Filter size={14} />
                    Filters
                    {activeFilterCount > 0 && (
                        <span className="ml-1 bg-white text-primary-600 text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                            {activeFilterCount}
                        </span>
                    )}
                </button>
                {activeFilterCount > 0 && (
                    <button onClick={clearAll} className={`text-sm ${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}`}>
                        Clear all
                    </button>
                )}
                <div className="flex items-center gap-1 ml-auto">
                    <button
                        onClick={() => handleDownload('excel')}
                        className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border font-medium ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                    >
                        <Download size={14} /> Excel
                    </button>
                    <button
                        onClick={() => handleDownload('pdf')}
                        className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border font-medium ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                    >
                        <Download size={14} /> PDF
                    </button>
                </div>
            </div>

            {/* Filters panel */}
            {showFilters && (
                <div className={`rounded-lg border p-4 grid grid-cols-2 gap-4 ${isDarkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                    <div>
                        <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Location</label>
                        <input
                            value={filters.location}
                            onChange={e => handleFilterChange('location', e.target.value)}
                            placeholder="e.g. New York"
                            className={`${inputCls} w-full`}
                        />
                    </div>
                    <div>
                        <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Time Type</label>
                        <select value={filters.timeType} onChange={e => handleFilterChange('timeType', e.target.value)} className={`${inputCls} w-full`}>
                            <option value="">All Types</option>
                            {TIME_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                </div>
            )}

            {/* Active filter pills */}
            {(filters.location || filters.timeType) && (
                <div className="flex flex-wrap gap-2">
                    {filters.location && (
                        <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                            Location: {filters.location}
                            <button onClick={() => handleFilterChange('location', '')}><X size={11} /></button>
                        </span>
                    )}
                    {filters.timeType && (
                        <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                            {filters.timeType}
                            <button onClick={() => handleFilterChange('timeType', '')}><X size={11} /></button>
                        </span>
                    )}
                </div>
            )}

            {/* Table */}
            {loading ? (
                <p className={`text-sm py-8 text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Loading…</p>
            ) : jobs.length === 0 ? (
                <div className="py-8 text-center space-y-1">
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>No job listings found.</p>
                    <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Please insert some jobs.</p>
                </div>
            ) : (
                <div className={`rounded-lg border overflow-hidden ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <table className="w-full">
                        <thead className={isDarkMode ? 'bg-slate-700/60' : 'bg-slate-50'}>
                            <tr>
                                <th className={thCls} onClick={() => handleSort('title')}>
                                    Title <SortIcon field="title" sortBy={sortBy} />
                                </th>
                                <th className={thCls} onClick={() => handleSort('location')}>
                                    Location <SortIcon field="location" sortBy={sortBy} />
                                </th>
                                <th className={thCls} onClick={() => handleSort('type')}>
                                    Type <SortIcon field="type" sortBy={sortBy} />
                                </th>
                                <th className={thCls} onClick={() => handleSort('posted')}>
                                    Posted <SortIcon field="posted" sortBy={sortBy} />
                                </th>
                                <th className={thCls}>Status</th>
                                <th className={thCls}></th>
                            </tr>
                        </thead>
                        <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-slate-100'}`}>
                            {jobs.map(job => (
                                <tr key={job.JobListingID} className={isDarkMode ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50'}>
                                    <td className={`${tdCls} font-medium`}>{job.JobTitle}</td>
                                    <td className={tdCls}>{job.JobLocation || '—'}</td>
                                    <td className={tdCls}>{job.TimeType || '—'}</td>
                                    <td className={tdCls}>
                                        {job.JobPostedOn ? new Date(job.JobPostedOn).toLocaleDateString() : '—'}
                                    </td>
                                    <td className={tdCls}>
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                            job.Status === 'active'
                                                ? 'bg-primary-100 text-primary-700'
                                                : job.Status === 'retired'
                                                    ? 'bg-slate-100 text-slate-500'
                                                    : 'bg-yellow-100 text-yellow-700'
                                        }`}>
                                            {job.Status}
                                        </span>
                                    </td>
                                    <td className={tdCls}>
                                        {job.JobLinkURL && (
                                            <a href={job.JobLinkURL} target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:text-primary-600">
                                                <ExternalLink size={14} />
                                            </a>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between">
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {total} listing{total !== 1 ? 's' : ''}
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page <= 1}
                            className={`p-1 rounded disabled:opacity-40 ${isDarkMode ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            {page} / {totalPages}
                        </span>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages}
                            className={`p-1 rounded disabled:opacity-40 ${isDarkMode ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function EmployerDashboard() {
    const { isDarkMode } = useTheme();
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('reporting');

    const tabs = [
        { id: 'reporting', label: 'Dashboard',        icon: BarChart3 },
        { id: 'imports',   label: 'Imports',          icon: Upload    },
        { id: 'jobs',      label: 'Job Listings',     icon: Briefcase },
        { id: 'company',   label: 'Company Details',  icon: Building2 },
    ];

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Page header */}
            <div>
                <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                    Employer Dashboard
                </h1>
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Welcome{user?.first_name ? `, ${user.first_name}` : ''}.
                </p>
            </div>

            {/* Tabs */}
            <div className={`rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                <div className={`flex border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
                                activeTab === tab.id
                                    ? 'text-primary-500 border-b-2 border-primary-500'
                                    : isDarkMode ? 'text-slate-400 hover:text-slate-100' : 'text-slate-600 hover:text-slate-900'
                            }`}
                        >
                            <tab.icon size={17} />
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="p-6">
                    {activeTab === 'imports' && <ExcelImport mode="employer" />}
                    {activeTab === 'reporting' && <EmployerReportingTab employerId={null} isDarkMode={isDarkMode} />}
                    {activeTab === 'jobs' && <EmployerJobListingsTab isDarkMode={isDarkMode} />}
                    {activeTab === 'company' && <CompanyDetailsTab isDarkMode={isDarkMode} />}
                </div>
            </div>
        </div>
    );
}
